/**
 * WindowPicker — the time-window control the model pages share (TRA-1436,
 * TRA-1461): a row of pills for the common windows and a box beside them
 * for any other length, typed as hours or days ("36h", "5d").
 *
 * The box applies on Enter and on blur; an empty box applies nothing. A bad
 * value keeps the current window and lights the box, and the page says why
 * wherever it wants with `WindowBoxError`. The box's state is `useWindowBox`.
 */

import { PillToggle } from '@/components/models/home/PillToggle'
import type { WindowBox } from '@/components/models/home/useWindowBox'
import { Input } from '@/components/ui/input'

export interface WindowOption {
  /** The window's hours, as a string so PillToggle can key on it. */
  value: string
  label: string
}

interface WindowPickerProps {
  options: WindowOption[]
  hours: number
  onChange: (hours: number) => void
  box: WindowBox
  pillsLabel: string
  boxLabel: string
  /** The id of the `WindowBoxError` line, read with the box while it is lit. */
  errorId: string
}

export function WindowPicker({
  options,
  hours,
  onChange,
  box,
  pillsLabel,
  boxLabel,
  errorId,
}: WindowPickerProps): React.JSX.Element {
  return (
    <div className="flex items-center gap-3">
      <PillToggle
        aria-label={pillsLabel}
        value={String(hours)}
        onChange={(value) => {
          onChange(Number(value))
          box.reset()
        }}
        options={options}
      />
      {/* Sized and lettered to sit flush with the h-6 pills beside it. */}
      <Input
        type="text"
        value={box.value}
        placeholder="36h or 3d"
        aria-label={boxLabel}
        aria-invalid={box.invalid}
        aria-describedby={box.invalid ? errorId : undefined}
        className="h-6 w-24 rounded-sm px-2 font-mono text-[10px] uppercase tracking-wider md:text-[10px]"
        onChange={(e) => {
          box.setValue(e.target.value)
        }}
        onBlur={box.apply}
        onKeyDown={(e) => {
          if (e.key === 'Enter') box.apply()
        }}
      />
    </div>
  )
}

/** The one line that says what the box takes, shown only while it is lit. */
export function WindowBoxError({
  box,
  id,
  help,
}: {
  box: WindowBox
  id: string
  help: string
}): React.JSX.Element | null {
  if (!box.invalid) return null
  return (
    <p id={id} role="alert" className="mt-2 text-sm text-destructive">
      {help}
    </p>
  )
}
